import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Blend, Image as ImageIcon, PieChart, Plus, Search, Trash2, X } from 'lucide-react';
import { parseColor, rgbToHsl, hslToRgb, toHex, toRgbString } from './colorMath';
import { nearestToken, resolveTokens } from './cssTokens';
import {
  HARMONY_RULES, CUSTOM_RULE, generateHarmony, reseedFrom, applyPickedTo,
  MIN_COLORS, MAX_COLORS, DEFAULT_COLORS,
} from './harmony';
import HarmonyWheel from './HarmonyWheel';
import ContrastMatrix from './ContrastMatrix';
import { loadSampler, checkFile, fileToDataUrl, ACCEPTED_TYPES, MAX_SOURCE_MB } from './imageColors';
import ColorPickerModal from './ColorPickerModal';

/* ─────────────────────────────────────────────────────────────
   Color Harmony — the second tool in the colorpicker family.

   The picker answers "what is this one colour". This answers
   "what set goes together, and is that set legible". Harmony
   builds the set, Contrast judges it, and the tile strip along
   the bottom belongs to both so switching tabs never loses it.

   Stateless by design: colours in, colours out. It has no idea
   what a theme is — no persistence, no naming, no token mapping,
   no apply-to-app. A caller that wants those composes this with
   its own page. That boundary is why the tool is reusable at all.
─────────────────────────────────────────────────────────────── */

const FALLBACK_SEED = { h: 195, s: 0.6, l: 0.25 };
const L_MIN = 8;
const L_MAX = 92;
const LOUPE_RADIUS = 4;          // 9x9 neighbourhood
const LOUPE_PX = 11;             // rendered size of one sampled pixel
const LOUPE_SPAN = LOUPE_RADIUS * 2 + 1;
const LOUPE_SIZE = LOUPE_SPAN * LOUPE_PX;

const toCss = hsl => toHex(hslToRgb(hsl));

const parseToHsl = value => {
  const rgba = parseColor(value);
  if (!rgba) return null;
  const { h, s, l } = rgbToHsl(rgba);
  return { h, s, l };
};

const ColorHarmonyModal = ({
  isOpen = false,
  title = 'Color Harmony',
  seed = '',
  colors: incomingColors = null,
  count: incomingCount = DEFAULT_COLORS,
  onClose,
  onApply,
}) => {
  const [tab, setTab] = useState('harmony');
  const [source, setSource] = useState('wheel');
  const [rule, setRule] = useState('triad');
  const [count, setCount] = useState(DEFAULT_COLORS);
  const [colors, setColors] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [editIndex, setEditIndex] = useState(null);

  const [imageSrc, setImageSrc] = useState(null);
  const [sampler, setSampler] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [marks, setMarks] = useState([]);          // where each colour was sampled
  const [loupe, setLoupe] = useState(false);
  const [loupeHex, setLoupeHex] = useState(null);  // last reading, held
  const fileRef = useRef(null);
  const imgRef = useRef(null);
  const wrapRef = useRef(null);
  const loupeRef = useRef(null);
  const rafRef = useRef(null);

  /* Seed the set once per open. */
  useEffect(() => {
    if (!isOpen) return;
    const n = Math.round(Math.min(MAX_COLORS, Math.max(MIN_COLORS, incomingCount)));
    const reset = () => {
      setTab('harmony');
      setSource('wheel');
      setActiveIndex(0);
      setImageSrc(null);
      setSampler(null);
      setImageError(null);
      setMarks([]);
      setLoupeHex(null);
    };
    setCount(n);
    const parsed = Array.isArray(incomingColors)
      ? incomingColors.map(parseToHsl).filter(Boolean)
      : [];
    if (parsed.length) {
      setColors(parsed);
      setRule(CUSTOM_RULE.id);
    } else {
      setRule('triad');
      setColors(generateHarmony(parseToHsl(seed) || FALLBACK_SEED, 'triad', n));
    }
    reset();
  }, [isOpen, seed, incomingColors, incomingCount]);

  /* Escape closes this dialog, but only when the picker is not
     stacked on top of it — that one owns the key while it is open. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = e => { if (e.key === 'Escape' && editIndex === null) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, editIndex]);

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  const regenerate = useCallback((nextRule, nextCount, fromIndex = 0) => {
    setColors(prev => {
      const anchor = prev[fromIndex] || prev[0] || FALLBACK_SEED;
      const base = reseedFrom(fromIndex, anchor, nextRule);
      return generateHarmony(base, nextRule, nextCount);
    });
    /* Every colour is derived afresh, so the sample dots would now point
       at places these colours did not come from. Drop them. */
    setMarks([]);
  }, []);

  const pickRule = nextRule => {
    setRule(nextRule);
    if (nextRule !== CUSTOM_RULE.id) regenerate(nextRule, count, 0);
  };

  const pickCount = nextCount => {
    setCount(nextCount);
    if (rule === CUSTOM_RULE.id) {
      setColors(prev => {
        if (nextCount <= prev.length) return prev.slice(0, nextCount);
        const last = prev[prev.length - 1] || FALLBACK_SEED;
        return [...prev, ...Array.from({ length: nextCount - prev.length }, () => ({ ...last }))];
      });
    } else {
      regenerate(rule, nextCount, 0);
    }
  };

  /* A handle moved. Under a rule the whole set is rebuilt from the
     seed that move implies, which is what keeps the geometry rigid.
     Under Custom only the dragged handle moves. */
  const handleMove = useCallback((index, { h, s }) => {
    setColors(prev => {
      const current = prev[index];
      if (!current) return prev;
      if (rule === CUSTOM_RULE.id) {
        const next = [...prev];
        next[index] = { ...current, h, s };
        return next;
      }
      const base = reseedFrom(index, { h, s, l: current.l }, rule);
      /* Lightness is the tile's own business — dragging sets hue and
         saturation only, so carry the existing values across. */
      return generateHarmony(base, rule, prev.length)
        .map((c, i) => ({ ...c, l: prev[i] ? prev[i].l : c.l }));
    });
  }, [rule]);

  /**
   * A known colour was chosen for one tile. Under a rule this must
   * RESEED rather than break the set: treat the choice as that
   * handle's position, derive the seed it implies, and let every
   * other colour move to keep the relationship. Only Custom edits
   * a single tile in isolation.
   */
  const applyPickedColor = (index, picked) => {
    if (!picked) return;
    setColors(prev => (rule === CUSTOM_RULE.id
      ? prev.map((c, i) => (i === index ? picked : c))
      : applyPickedTo(prev, index, picked, rule)));
  };

  const setLightness = (index, pct) => {
    const l = Math.min(L_MAX, Math.max(L_MIN, pct)) / 100;
    setColors(prev => prev.map((c, i) => (i === index ? { ...c, l } : c)));
  };

  const setAllLightness = pct => {
    const l = Math.min(L_MAX, Math.max(L_MIN, pct)) / 100;
    setColors(prev => prev.map(c => ({ ...c, l })));
  };

  const removeAt = index => {
    if (colors.length <= MIN_COLORS) return;
    setColors(prev => prev.filter((_, i) => i !== index));
    setMarks(prev => prev.filter(mk => mk.index !== index).map(mk => (
      mk.index > index ? { ...mk, index: mk.index - 1 } : mk
    )));
    setCount(c => Math.max(MIN_COLORS, c - 1));
    setRule(CUSTOM_RULE.id);
    setActiveIndex(i => Math.max(0, Math.min(i, colors.length - 2)));
  };

  /* ── Image source ─────────────────────────────────────────── */

  const acceptFile = async file => {
    const problem = checkFile(file);
    if (problem) { setImageError(problem); return; }
    setImageError(null);
    try {
      const dataUrl = await fileToDataUrl(file);
      setImageSrc(dataUrl);
      setMarks([]);
      setSampler(await loadSampler(dataUrl));
    } catch (err) {
      setImageError(err.message || 'Could not read that image');
    }
  };

  useEffect(() => {
    if (!isOpen || tab !== 'harmony' || source !== 'image') return undefined;
    const onPaste = e => {
      const item = [...(e.clipboardData?.items || [])].find(i => i.type.startsWith('image/'));
      if (item) acceptFile(item.getAsFile());
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [isOpen, tab, source]);

  /* Coordinates are CLAMPED to the image, and tracked against the
     padded wrapper rather than the image itself. That padding is the
     whole point: to sample a pixel on the very edge the pointer has to
     sit on the boundary, and any overshoot used to leave the image and
     kill the loupe. Now it overshoots into the padding and clamps, so
     edge and corner pixels are actually reachable. */
  const relFromEvent = e => {
    const ibox = imgRef.current.getBoundingClientRect();
    const wbox = wrapRef.current.getBoundingClientRect();
    const unit = v => Math.min(1, Math.max(0, v));
    return {
      relX: unit((e.clientX - ibox.left) / ibox.width),
      relY: unit((e.clientY - ibox.top) / ibox.height),
      x: e.clientX - wbox.left,
      y: e.clientY - wbox.top,
    };
  };

  /* The magnifier is drawn straight onto a tiny canvas and scaled up
     by CSS, rather than rendered as a grid of elements — one
     putImageData per move instead of eighty-one DOM updates. */
  const paintLoupe = useCallback((relX, relY) => {
    const canvas = loupeRef.current;
    if (!canvas || !sampler) return;
    const { span, pixels } = sampler.sampleRegion(relX, relY, LOUPE_RADIUS);
    const ctx = canvas.getContext('2d');
    const image = ctx.createImageData(span, span);
    pixels.forEach((p, i) => {
      image.data[i * 4] = p.r;
      image.data[i * 4 + 1] = p.g;
      image.data[i * 4 + 2] = p.b;
      image.data[i * 4 + 3] = 255;
    });
    ctx.putImageData(image, 0, 0);
  }, [sampler]);

  const onImageMove = e => {
    if (!loupe || !sampler || !imgRef.current || !wrapRef.current) return;
    const { relX, relY } = relFromEvent(e);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      paintLoupe(relX, relY);
      setLoupeHex(toHex(sampler.sampleAt(relX, relY)));
    });
  };

  const sampleFromImage = e => {
    if (!sampler || !imgRef.current || !wrapRef.current) return;
    const { relX, relY } = relFromEvent(e);
    const rgb = sampler.sampleAt(relX, relY);
    const { h, s, l } = rgbToHsl(rgb);
    const index = activeIndex;

    /* Sampling is per-colour by nature: you are choosing this swatch,
       not a seed. Under a rule it would reseed the whole set on every
       click and wipe out the colours already picked, so the first
       sample releases the rule. Going back to the wheel and choosing a
       rule then re-derives everything from colour 1, which is the
       normal anchor. */
    setRule(CUSTOM_RULE.id);
    setColors(prev => prev.map((c, i) => (i === index ? { h, s, l } : c)));

    /* Leave a dot where it was taken, so the set is traceable back
       to the image rather than being a strip of anonymous colours. */
    setMarks(prev => [...prev.filter(mk => mk.index !== index), { index, relX, relY }]);
    setActiveIndex(i => (i + 1) % Math.max(1, colors.length));
  };

  /* ── Output ───────────────────────────────────────────────── */

  const hexes = useMemo(() => colors.map(toCss), [colors]);
  const rgbaSet = useMemo(() => colors.map(c => ({ ...hslToRgb(c), a: 1 })), [colors]);

  /* Resolve the token table ONCE per open and pass it in. nearestToken
     defaults to calling resolveTokens() itself, which walks every rule
     in every stylesheet — doing that per colour per render turns a
     handle drag into a full CSSOM enumeration at pointer-move rate and
     locks the main thread. */
  const tokenTable = useMemo(() => (isOpen ? resolveTokens() : []), [isOpen]);
  const tokens = useMemo(
    () => rgbaSet.map(c => nearestToken(c, tokenTable)),
    [rgbaSet, tokenTable],
  );

  const commonLightness = colors.length
    ? Math.round((colors.reduce((a, c) => a + c.l, 0) / colors.length) * 100)
    : 50;
  const lightnessVaries = colors.some(c => Math.round(c.l * 100) !== commonLightness);

  const apply = () => {
    onApply?.(hexes, colors.map((c, i) => ({
      hex: hexes[i],
      hsl: { ...c },
      token: tokens[i] && tokens[i].exact ? tokens[i].token : null,
    })));
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="stui-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="stui-modal stui-ch-modal">
        <div className="stui-modal-header">
          <h3><Blend size={18} className="stui-ch-title-icon" />{title}</h3>
          <button className="stui-modal-close" onClick={onClose} title="Close"><X size={20} /></button>
        </div>

        <div className="stui-cp-tabbar">
          <button type="button" className={`stui-cp-tab${tab === 'harmony' ? ' active' : ''}`} onClick={() => setTab('harmony')}>
            Harmony
          </button>
          <button type="button" className={`stui-cp-tab${tab === 'contrast' ? ' active' : ''}`} onClick={() => setTab('contrast')}>
            Contrast
          </button>
        </div>

        <div className="stui-modal-body stui-ch-body">
          {tab === 'harmony' && (
            <>
              <div className="stui-ch-sourcerow">
                <div className="stui-pill-group">
                  <button type="button" className={`stui-pill${source === 'wheel' ? ' selected' : ''}`} onClick={() => setSource('wheel')}>
                    <PieChart size={12} /> Wheel
                  </button>
                  <button type="button" className={`stui-pill${source === 'image' ? ' selected' : ''}`} onClick={() => setSource('image')}>
                    <ImageIcon size={12} /> Image
                  </button>
                </div>
                <label className="stui-ch-countrow">
                  <span className="stui-label-xs">Colors</span>
                  <input
                    type="number"
                    className="stui-cp-num"
                    min={MIN_COLORS}
                    max={MAX_COLORS}
                    value={count}
                    onChange={e => pickCount(Math.min(MAX_COLORS, Math.max(MIN_COLORS, Number(e.target.value))))}
                  />
                </label>
              </div>

              {source === 'wheel' && (
                <div className="stui-ch-wheelrow">
                  <HarmonyWheel
                    colors={colors}
                    activeIndex={activeIndex}
                    onHandleMove={handleMove}
                    onHandlePick={setActiveIndex}
                  />
                  <div className="stui-ch-rules">
                    {[...HARMONY_RULES, CUSTOM_RULE].map(r => (
                      <button
                        key={r.id}
                        type="button"
                        className={`stui-ch-rule${rule === r.id ? ' selected' : ''}`}
                        onClick={() => pickRule(r.id)}
                        title={r.id === CUSTOM_RULE.id ? 'Release the handles to move independently' : r.label}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {source === 'image' && (
                <div className="stui-ch-imagepane">
                  {!imageSrc && (
                    <div
                      className={`stui-ch-drop${dragOver ? ' over' : ''}`}
                      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={e => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer.files[0]); }}
                      onClick={() => fileRef.current?.click()}
                    >
                      <ImageIcon size={32} className="stui-ch-dropicon" />
                      <p className="stui-ch-droptitle">Drop an image, paste, or click to browse</p>
                      <p className="stui-ch-dropsub">Then click the image to sample colors · max {MAX_SOURCE_MB}MB</p>
                      <input
                        ref={fileRef}
                        type="file"
                        accept={ACCEPTED_TYPES}
                        className="stui-ch-fileinput"
                        onChange={e => { acceptFile(e.target.files[0]); e.target.value = ''; }}
                      />
                    </div>
                  )}
                  {imageSrc && (
                    <>
                      <div className="stui-ch-imagerow">
                      {/* Handlers live on the PADDED wrapper, not the image, so
                          the pointer can overshoot the edge and still sample the
                          boundary pixel instead of dropping out. */}
                      <div
                        className="stui-ch-imagewrap"
                        ref={wrapRef}
                        onClick={sampleFromImage}
                        onMouseMove={onImageMove}
                      >
                        <div className="stui-ch-imagebox">
                          <img ref={imgRef} src={imageSrc} alt="Sample source" className="stui-ch-image" />
                          {/* Where each colour was taken from. */}
                          {marks.map(mk => (
                            <span
                              key={mk.index}
                              className={`stui-ch-mark${mk.index === activeIndex ? ' active' : ''}`}
                              style={{
                                left: `${mk.relX * 100}%`,
                                top: `${mk.relY * 100}%`,
                                background: colors[mk.index] ? toRgbString(hslToRgb(colors[mk.index])) : 'transparent',
                              }}
                              title={`Color ${mk.index + 1} — ${hexes[mk.index] || ''}`}
                            >
                              {mk.index + 1}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* The magnifier does NOT follow the cursor. Anchored
                          beside the image it cannot be clipped, cannot run off
                          an edge, and does not vanish when the pointer strays
                          past the boundary — which is exactly where you are
                          working when you need it. It holds the last reading
                          rather than clearing, so a value stays readable after
                          you move away. */}
                      {loupe && (
                        <div className="stui-ch-loupepanel">
                          <div className="stui-ch-loupeframe" style={{ width: LOUPE_SIZE, height: LOUPE_SIZE }}>
                            <canvas
                              ref={loupeRef}
                              width={LOUPE_SPAN}
                              height={LOUPE_SPAN}
                              className="stui-ch-loupecanvas"
                              style={{ width: LOUPE_SIZE, height: LOUPE_SIZE }}
                            />
                            <span className="stui-ch-loupecentre" style={{ width: LOUPE_PX, height: LOUPE_PX }} />
                          </div>
                          <div className="stui-ch-loupereadout">
                            <span className="stui-ch-loupeswatch" style={{ background: loupeHex || 'transparent' }} />
                            <code>{loupeHex || '—'}</code>
                          </div>
                        </div>
                      )}
                      </div>
                      <div className="stui-ch-imagehint">
                        <span className="stui-hint">
                          Click to fill swatch {activeIndex + 1}, then it advances to the next.
                        </span>
                        <div className="stui-ch-imageactions">
                          <button
                            type="button"
                            className={`stui-pill${loupe ? ' selected' : ''}`}
                            onClick={() => { setLoupe(v => !v); setLoupeHex(null); }}
                            title="Magnify the pixels under the cursor"
                          >
                            <Search size={12} /> Loupe
                          </button>
                          <button type="button" className="stui-btn stui-btn-secondary" onClick={() => { setImageSrc(null); setSampler(null); setMarks([]); setLoupeHex(null); }}>
                            <Trash2 size={12} /> Clear image
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                  {imageError && <p className="stui-ch-imageerror">{imageError}</p>}
                </div>
              )}
            </>
          )}

          {tab === 'contrast' && <ContrastMatrix colors={rgbaSet} />}
        </div>

        {/* The set itself — owned by neither tab, visible from both. */}
        <div className="stui-ch-setbar">
          <label className="stui-ch-alll">
            <span className="stui-label-xs">Lightness — all</span>
            <input
              type="range"
              className="stui-ch-allslider"
              min={L_MIN}
              max={L_MAX}
              value={commonLightness}
              onChange={e => setAllLightness(Number(e.target.value))}
              aria-label="Lightness of every color"
            />
            <input
              type="number"
              className="stui-cp-num"
              min={L_MIN}
              max={L_MAX}
              value={commonLightness}
              onChange={e => setAllLightness(Number(e.target.value))}
            />
            <span className="stui-cp-numsuffix">%{lightnessVaries ? ' (avg)' : ''}</span>
          </label>

          <div className="stui-ch-tiles">
            {colors.map((c, i) => (
              <div key={i} className={`stui-ch-tile${i === activeIndex ? ' active' : ''}`}>
                <button
                  type="button"
                  className="stui-ch-tileswatch"
                  style={{ background: toRgbString(hslToRgb(c)) }}
                  onClick={() => { setActiveIndex(i); setEditIndex(i); }}
                  title={`${hexes[i]} — click to pick a known color${rule === CUSTOM_RULE.id ? '' : '; the rest follow the rule'}${tokens[i] && tokens[i].exact ? ` (${tokens[i].token})` : ''}`}
                />
                <div className="stui-ch-lrow">
                  <span className="stui-ch-llabel" title="Lightness">L</span>
                  <input
                    type="range"
                    className="stui-ch-tilelight"
                    min={L_MIN}
                    max={L_MAX}
                    value={Math.round(c.l * 100)}
                    onChange={e => setLightness(i, Number(e.target.value))}
                    aria-label={`Lightness of color ${i + 1}`}
                  />
                  <input
                    type="number"
                    className="stui-ch-lnum"
                    min={L_MIN}
                    max={L_MAX}
                    value={Math.round(c.l * 100)}
                    onChange={e => setLightness(i, Number(e.target.value))}
                    aria-label={`Lightness percent of color ${i + 1}`}
                  />
                </div>
                <code className="stui-ch-tilehex">{hexes[i]}</code>
                {tokens[i] && tokens[i].exact && <code className="stui-ch-tiletoken">{tokens[i].token}</code>}
                {colors.length > MIN_COLORS && (
                  <button type="button" className="stui-ch-tiledrop" onClick={() => removeAt(i)} title="Remove">
                    <X size={11} />
                  </button>
                )}
              </div>
            ))}
            {colors.length < MAX_COLORS && (
              <button
                type="button"
                className="stui-ch-tileadd"
                onClick={() => pickCount(colors.length + 1)}
                title="Add a color"
              >
                <Plus size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="stui-modal-footer">
          <button type="button" className="stui-btn stui-btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="stui-btn stui-btn-primary" onClick={apply}>Use these colors</button>
        </div>
      </div>
    </div>

      {/* Sibling of the overlay above, not a child of it — a fixed element
          with a z-index opens its own stacking context, so nesting the
          picker inside would trap .stui-modal-overlay-nested's 1100 in a
          local comparison against 1000's children instead of against 1000
          itself. As siblings the tier does the work the CSS says it does. */}
      <ColorPickerModal
        isOpen={editIndex !== null}
        nested
        title={`Color ${(editIndex ?? 0) + 1}`}
        value={editIndex !== null ? hexes[editIndex] : ''}
        allowAlpha={false}
        onClose={() => setEditIndex(null)}
        onSelect={result => {
          if (editIndex === null) return;
          applyPickedColor(editIndex, parseToHsl(result.hex));
        }}
      />
    </>
  );
};

export default ColorHarmonyModal;
